import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, AlertCircle, Loader2 } from 'lucide-react';
import { ItemSearchModal } from '@/app/pages/sales-orders/components/ItemSearchModal';
import { stockTransferFormSchema, StockTransferFormValues } from './schema';
import { StockTransferOrder } from '@/app/types/stockTransfer';
import { Godown } from '@/app/types/inventory';
import { toast } from 'sonner';

interface StockTransferFormProps {
  initialData?: StockTransferOrder | null;
  godowns: Godown[];
  onSubmit: (values: StockTransferFormValues) => void;
  isSubmitting: boolean;
  disabled?: boolean;
  checkItemStock?: (itemId: number, godownId: number) => Promise<number>;
  prefilledItems?: any[];
}

export const StockTransferForm: React.FC<StockTransferFormProps> = ({
  initialData,
  godowns,
  onSubmit,
  isSubmitting,
  disabled = false,
  checkItemStock,
  prefilledItems,
}) => {
  const [isItemSearchOpen, setIsItemSearchOpen] = useState(false);
  const [selectedItemIndex, setSelectedItemIndex] = useState<number | null>(null);
  const [itemStocks, setItemStocks] = useState<Record<string, number>>({});
  const [loadingStocks, setLoadingStocks] = useState<Record<string, boolean>>({});

  const form = useForm<StockTransferFormValues>({
    resolver: zodResolver(stockTransferFormSchema),
    defaultValues: {
      source_godown_id: initialData?.source_godown_id || 0, // Let user select source godown
      destination_godown_id: initialData?.destination_godown_id || prefilledItems?.[0]?.destination_godown_id || 0,
      transaction_date: initialData?.transaction_date || format(new Date(), 'yyyy-MM-dd'),
      reference_number: initialData?.reference_number || '',
      notes: initialData?.notes || (prefilledItems ? 'Internal transfer to replenish low stock items' : ''),
      items: initialData?.items?.map(item => ({
        id: item.id,
        item_id: item.item_id,
        item: item.item,
        quantity: typeof item.quantity === 'string' ? parseFloat(item.quantity) : item.quantity,
        rate: typeof item.rate === 'string' ? parseFloat(item.rate) : item.rate,
        unit_id: item.unit_id,
        notes: item.notes || '',
      })) || prefilledItems?.map(item => ({
        item_id: item.item_id,
        item: item.item,
        quantity: item.quantity || 1,
        rate: item.rate || 0,
        unit_id: item.unit_id || 1,
        notes: item.notes || '',
      })) || [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'items',
  });

  const sourceGodownId = form.watch('source_godown_id');

  // Check stock when source godown or items change
  useEffect(() => {
    if (!sourceGodownId || !checkItemStock) return;

    const checkStocks = async () => {
      const items = form.getValues('items');
      const newStocks: Record<string, number> = {};
      const newLoading: Record<string, boolean> = {};

      for (const item of items) {
        if (item.item_id) {
          const key = `${item.item_id}_${sourceGodownId}`;
          newLoading[key] = true;
          setLoadingStocks(prev => ({ ...prev, [key]: true }));

          try {
            const stock = await checkItemStock(item.item_id, sourceGodownId);
            newStocks[key] = stock;
          } catch (error) {
            console.error('Failed to check stock:', error);
            newStocks[key] = 0;
          } finally {
            newLoading[key] = false;
          }
        }
      }

      setItemStocks(prev => ({ ...prev, ...newStocks }));
      setLoadingStocks(prev => {
        const updated = { ...prev };
        Object.keys(newLoading).forEach(key => {
          updated[key] = newLoading[key];
        });
        return updated;
      });
    };

    checkStocks();
  }, [sourceGodownId, fields.length, checkItemStock]);

  const handleAddItem = () => {
    setSelectedItemIndex(fields.length);
    setIsItemSearchOpen(true);
  };

  const handleSelectItem = (item: any) => {
    const newItem = {
      item_id: item.id,
      item: {
        id: item.id,
        name: item.name,
        sku: item.code || item.sku,
        unit: item.unit,
      },
      quantity: 1,
      rate: item.rate || 0,
      unit_id: item.unit?.id || item.unit_id || 1,
      notes: '',
    };

    if (selectedItemIndex !== null && selectedItemIndex < fields.length) {
      // Update existing item
      form.setValue(`items.${selectedItemIndex}`, newItem);
    } else {
      // Add new item
      append(newItem);
    }

    setIsItemSearchOpen(false);
    setSelectedItemIndex(null);
  };

  const handleRemoveItem = (index: number) => {
    remove(index);
  };

  const validateStock = (): boolean => {
    if (!sourceGodownId) {
      toast.error('Please select a source godown first');
      return false;
    }

    const items = form.getValues('items');
    let hasStockIssues = false;

    items.forEach((item, index) => {
      const key = `${item.item_id}_${sourceGodownId}`;
      const availableStock = itemStocks[key] || 0;
      
      if (item.quantity > availableStock) {
        form.setError(`items.${index}.quantity`, {
          type: 'manual',
          message: `Insufficient stock. Available: ${availableStock}`,
        });
        hasStockIssues = true;
      }
    });

    if (hasStockIssues) {
      toast.error('Some items have insufficient stock. Please check quantities.');
      return false;
    }

    return true;
  };

  const handleSubmit = (values: StockTransferFormValues) => {
    // Check if godowns are selected
    if (!values.source_godown_id || values.source_godown_id === 0) {
      toast.error('Please select a source godown');
      return;
    }
    if (!values.destination_godown_id || values.destination_godown_id === 0) {
      toast.error('Please select a destination godown');
      return;
    }
    // Check if items exist
    if (!values.items || values.items.length === 0) {
      toast.error('Please add at least one item to transfer');
      return;
    }
    if (!validateStock()) return;
    onSubmit(values);
  };

  // Add form validation error handler
  const handleFormError = (errors: any) => {
    console.error('Form validation errors:', errors);
    console.log('Current form values:', form.getValues());
    
    if (errors.source_godown_id) {
      toast.error(errors.source_godown_id.message || 'Please select a source godown');
    } else if (errors.destination_godown_id) {
      toast.error(errors.destination_godown_id.message || 'Please select a destination godown');
    } else if (errors.items) {
      if (Array.isArray(errors.items) && errors.items.length > 0) {
        // Check for specific item errors
        const firstItemError = errors.items[0];
        if (firstItemError) {
          const errorField = Object.keys(firstItemError)[0];
          toast.error(`Item error: ${firstItemError[errorField]?.message || 'Invalid item data'}`);
        } else {
          toast.error('Please check item details');
        }
      } else if (errors.items.message) {
        toast.error(errors.items.message);
      } else {
        toast.error('Please add at least one item to transfer');
      }
    } else {
      toast.error('Please check the form for errors');
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit, handleFormError)} className="space-y-6">
        {/* Header Information */}
        <Card>
          <CardHeader>
            <CardTitle>Transfer Details</CardTitle>
            <CardDescription>Select source and destination godowns for the transfer</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="source_godown_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Source Godown *</FormLabel>
                  <Select
                    disabled={disabled}
                    onValueChange={(value) => field.onChange(Number(value))}
                    value={field.value ? String(field.value) : ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select source godown" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {godowns.map((godown) => (
                        <SelectItem key={godown.id} value={String(godown.id)}>
                          {godown.name} {godown.code && `(${godown.code})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="destination_godown_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Destination Godown *</FormLabel>
                  <Select
                    disabled={disabled}
                    onValueChange={(value) => field.onChange(Number(value))}
                    value={field.value ? String(field.value) : ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select destination godown" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {godowns
                        .filter(g => g.id !== form.watch('source_godown_id'))
                        .map((godown) => (
                          <SelectItem key={godown.id} value={String(godown.id)}>
                            {godown.name} {godown.code && `(${godown.code})`}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="transaction_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Transfer Date *</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      disabled={disabled}
                      {...field}
                      max={format(new Date(), 'yyyy-MM-dd')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reference_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reference Number</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Optional reference"
                      disabled={disabled}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>External reference or tracking number</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Items Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Items to Transfer</CardTitle>
                <CardDescription>Add items and quantities to transfer</CardDescription>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddItem}
                disabled={disabled || !sourceGodownId}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!sourceGodownId && (
              <div className="flex items-center gap-2 p-3 mb-4 text-sm text-warning bg-warning/5 rounded-md">
                <AlertCircle className="h-4 w-4" />
                <span>Please select a source godown first to add items</span>
              </div>
            )}

            {fields.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Available Stock</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fields.map((field, index) => {
                    const stockKey = `${field.item_id}_${sourceGodownId}`;
                    const availableStock = itemStocks[stockKey] || 0;
                    const isLoadingStock = loadingStocks[stockKey] || false;
                    const quantity = form.watch(`items.${index}.quantity`);
                    const hasInsufficientStock = quantity > availableStock;

                    return (
                      <TableRow key={field.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{field.item?.name}</div>
                            {field.item?.sku && (
                              <div className="text-sm text-muted-foreground">
                                SKU: {field.item.sku}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {isLoadingStock ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Badge variant={hasInsufficientStock ? 'destructive' : 'secondary'}>
                              {availableStock}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <FormField
                            control={form.control}
                            name={`items.${index}.quantity`}
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Input
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    disabled={disabled}
                                    {...field}
                                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                    className={hasInsufficientStock ? 'border-destructive' : ''}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <FormField
                            control={form.control}
                            name={`items.${index}.rate`}
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    disabled={disabled}
                                    {...field}
                                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          {field.item?.unit?.symbol || 'PCS'}
                        </TableCell>
                        <TableCell>
                          <FormField
                            control={form.control}
                            name={`items.${index}.notes`}
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Input
                                    placeholder="Optional notes"
                                    disabled={disabled}
                                    {...field}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveItem(index)}
                            disabled={disabled}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                No items added yet. Click "Add Item" to start.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notes Section */}
        <Card>
          <CardHeader>
            <CardTitle>Additional Information</CardTitle>
          </CardHeader>
          <CardContent>
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add any additional notes about this transfer..."
                      disabled={disabled}
                      rows={4}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Form Actions */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => window.history.back()}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={disabled || isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Transfer Order'
            )}
          </Button>
        </div>
      </form>

      {/* Item Search Modal */}
      <ItemSearchModal
        isOpen={isItemSearchOpen}
        onOpenChange={setIsItemSearchOpen}
        onSelectItem={handleSelectItem}
      />
    </Form>
  );
};